import { memo, lazy, Suspense } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react';
import useScrollAnimation from '@/hooks/useScrollAnimation';
import { trackLocationClick, trackContactClick } from '@/hooks/useGoogleAnalytics';
import { FormSkeleton } from '@/components/forms';
import { staggerDelay } from '@/lib/stagger';

// Lazy load ContactForm (includes Supabase + Zod - ~96KB savings)
const ContactForm = lazy(() => import('./ContactForm'));
const contactInfo = [
  {
    icon: Phone,
    label: 'Teléfono',
    value: '614 515 974',
    href: 'tel:+34614515974',
  },
  {
    icon: Mail,
    label: 'Email',
    value: 'marinaglez@mghnutricion.es',
    href: 'mailto:marinaglez@mghnutricion.es',
  },
  {
    icon: Clock,
    label: 'Horario',
    value: 'Lun - Vie: 9:00 - 20:00',
    href: null,
  },
] as const;

const locations = [
  {
    name: 'Humanes de Madrid',
    local: 'Farmacia Zazo',
    address: 'Plaza Miguel Echegaray 5, 28970',
    mapUrl: 'https://maps.app.goo.gl/FGmYa2ym5KwHNJMt6',
  },
  {
    name: 'Fuenlabrada',
    local: 'Zazo Fitness',
    address: 'Calle Pelayos 3, 28945',
    mapUrl: 'https://maps.app.goo.gl/3E5aMwDogSwLAnFd7',
  },
] as const;

interface ContactInfoCardProps {
  info: typeof contactInfo[number];
  index: number;
}

const ContactInfoCard = memo(({ info, index }: ContactInfoCardProps) => {
  const { ref, isVisible } = useScrollAnimation(0.1);
  const Icon = info.icon;

  const handleContactClick = () => {
    if (info.href) {
      const method = info.label === 'Teléfono' ? 'phone' : 'email';
      trackContactClick(method, info.value, 'Contact section');
    }
  };

  const content = (
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 bg-rose-light rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-rose-dark" aria-hidden="true" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{info.label}</p>
        <p className="font-medium text-foreground">{info.value}</p>
      </div>
    </div>
  );

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      } ${staggerDelay(index)}`}
    >
      {info.href ? (
        <a
          href={info.href}
          className="glass-card block hover:shadow-soft-lg"
          aria-label={`${info.label}: ${info.value}`}
          onClick={handleContactClick}
        >
          {content}
        </a>
      ) : (
        <div className="glass-card">{content}</div>
      )}
    </div>
  );
});

ContactInfoCard.displayName = 'ContactInfoCard';

const Contact = memo(() => {
  const { ref, isVisible } = useScrollAnimation();

  const handleLocationClick = (locationName: string, localName: string) => {
    trackLocationClick(locationName, localName);
  };

  return (
    <section id="contacto" className="section-padding gradient-section">
      <div className="container-custom">
        {/* Section Header */}
        <div
          ref={ref}
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <span className="inline-block text-primary font-medium mb-4">
            Contacto
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            Contacta con tu{' '}
            <span className="text-gradient">nutricionista</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Ponte en contacto conmigo y da el primer paso hacia 
            una vida más saludable.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {/* Quick contact cards */}
            <div className="space-y-4">
              {contactInfo.map((info, index) => (
                <ContactInfoCard key={info.label} info={info} index={index} />
              ))}
            </div>

            {/* Locations */}
            <div className="glass-card mt-8">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2" id="locations-heading">
                <MapPin className="w-5 h-5 text-primary" aria-hidden="true" />
                Consultas presenciales
              </h3>
              <div className="grid sm:grid-cols-2 gap-4" role="list" aria-labelledby="locations-heading">
                {locations.map((location) => (
                  <a 
                    key={location.name} 
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-muted/50 rounded-xl p-4 block hover:bg-muted/70 transition-all duration-300 hover:scale-[1.02]" 
                    role="listitem"
                    aria-label={`Ver ${location.local} en Google Maps`}
                    onClick={() => handleLocationClick(location.name, location.local)}
                  >
                    <p className="font-medium text-foreground mb-1">{location.name}</p>
                    <p className="text-sm text-primary font-medium">{location.local}</p>
                    <p className="text-sm text-muted-foreground">{location.address}</p>
                  </a>
                ))}
              </div>
            </div>

            {/* WhatsApp Card */}
            <a
              href="https://wa.me/34614515974?text=Hola%20Marina,%20me%20gustaría%20solicitar%20información%20sobre%20tus%20servicios%20de%20nutrición."
              target="_blank"
              rel="noopener noreferrer"
              className="glass-card mt-4 flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium py-4 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg"
              aria-label="Contactar por WhatsApp"
              onClick={() => trackContactClick('whatsapp', '614 515 974', 'Contact section')}
            >
              <MessageCircle className="w-6 h-6" aria-hidden="true" />
              <span className="text-lg">Contactar por WhatsApp</span>
            </a>
          </div>

          {/* Contact Form - Lazy loaded for performance */}
          <Suspense fallback={<FormSkeleton />}>
            <ContactForm />
          </Suspense>
        </div>
      </div>
    </section>
  );
});

Contact.displayName = 'Contact';

export default Contact;
